// ============================================================
// FILE: Sidebar.jsx — Permission-based, role-aware (all roles)
// ============================================================
import React, { useState } from 'react';
import {
  Menu, X, LogOut, LayoutDashboard, User, ListTodo,
  MessageSquare, Calendar, Ticket, Wrench, Users,
  Plug, AlertTriangle, UsersRound, PhoneCall,
  ChevronDown, ChevronRight, ClipboardList,
  FileBarChart2, Workflow, Shield, Loader2,
} from 'lucide-react';
import logo from "../Images/ORCA_SIDEBAR.png";
import { usePermissions } from '../hooks/usePermissions';

const Sidebar = ({
  isSidebarOpen,
  setIsSidebarOpen,
  isMobile,
  currentPage,
  handlePageChange,
  handleLogout
}) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const { canView, roleName, loading } = usePermissions();

  // ─── Role detection ───────────────────────────────────────
  const storedUserRole = (localStorage.getItem("userRole") || "").toLowerCase().trim();
  const adminData      = (() => { try { return JSON.parse(localStorage.getItem("admin") || "{}"); } catch { return {}; } })();
  const dbRole         = (adminData.role || "").toLowerCase().replace(/\s+/g, '_').trim();

  // isSuperAdmin — hardcoded superadmin login OR DB role is "Super Admin" / "Superadmin"
  const isSuperAdmin =
    storedUserRole === "superadmin" ||
    storedUserRole === "super_admin" ||
    dbRole         === "super_admin" ||
    dbRole         === "superadmin";

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => prev[menuId] ? {} : { [menuId]: true });
  };

  const MENU_ITEMS = [
    { id: 'dashboard',           icon: LayoutDashboard, label: 'Dashboard',   permKey: 'dashboard' },
    {
      id: 'profile',             icon: User,            label: 'Profile',     permKey: 'profile',
      hasSubmenu: true,
      submenu: [
        { id: 'ViewProfiles',    label: 'View Profile', permKey: 'ViewProfiles' },
        { id: 'ViewEditProfile', label: 'Edit Profile', permKey: 'ViewEditProfile' },
      ],
    },
    {
      id: 'task',                icon: ListTodo,        label: 'Task',        permKey: 'task',
      hasSubmenu: true,
      submenu: [
        { id: 'TaskTicket',     label: 'All Tasks',  permKey: 'TaskTicket' },
        { id: 'TicketPending',  label: 'Pending',    permKey: 'TicketPending' },
        { id: 'TicketComplete', label: 'Completed',  permKey: 'TicketComplete' },
      ],
    },
    { id: 'AdminMessenger',      icon: MessageSquare,   label: 'Chat',        permKey: 'AdminMessenger' },
    { id: 'CalendarApp',         icon: Calendar,        label: 'Calendar',    permKey: 'CalendarApp' },
    {
      id: 'ticketing',           icon: Ticket,          label: 'Ticketing',   permKey: 'ticketing',
      hasSubmenu: true,
      submenu: [
        { id: 'TicketsPage',    label: 'New Ticket',     permKey: 'TicketsPage' },
        { id: 'OpenTickets',    label: 'Open Tickets',   permKey: 'OpenTickets' },
        { id: 'ClosedTickets',  label: 'Closed Tickets', permKey: 'ClosedTickets' },
      ],
    },
    { id: 'maintenance',         icon: Wrench,          label: 'Maintenance', permKey: 'maintenance' },
    {
      id: 'customer',            icon: Users,           label: 'Customer',    permKey: 'customer',
      hasSubmenu: true,
      submenu: [
        { id: 'CustomerList',    label: 'Customer List', permKey: 'CustomerList' },
        { id: 'AddCustomerList', label: 'Add Customer',  permKey: 'AddCustomerList' },
      ],
    },
    { id: 'Plugin',              icon: Plug,            label: 'Plugins',     permKey: 'Plugin' },
    { id: 'Incident',            icon: AlertTriangle,   label: 'Incidents',   permKey: 'Incident' },
    { id: 'UserAdminManagement', icon: UsersRound,      label: 'Users',       permKey: 'UserAdminManagement' },
    { id: 'Oncall',              icon: PhoneCall,       label: 'On-call',     permKey: 'Oncall' },
    { id: 'ReviewContact',       icon: PhoneCall,       label: 'Contact',     permKey: 'ReviewContact' },
    { id: 'Workload',            icon: FileBarChart2,   label: 'Reports',     permKey: 'Workload' },
    { id: 'FlowchartBuilder',    icon: Workflow,        label: 'Flow Chart',  permKey: 'FlowchartBuilder' },
    {
      id: 'KanbanBoard',         icon: ClipboardList,   label: 'Create Plan', permKey: 'KanbanBoard',
      hasSubmenu: true,
      submenu: [
        { id: 'BoardView', label: 'Board View', permKey: 'BoardView' },
        { id: 'ListView',  label: 'List View',  permKey: 'ListView' },
        { id: 'MyCards',   label: 'My Cards',   permKey: 'MyCards' },
        { id: 'Archived',  label: 'Archived',   permKey: 'Archived' },
      ],
    },
    // Super Admin only
    {
      id: 'PermissionManager',
      icon: Shield,
      label: 'Permissions',
      permKey: 'PermissionManager',
      superAdminOnly: true,
    },
  ];

  const visibleMenus = MENU_ITEMS.filter(item => {
    if (item.superAdminOnly) return isSuperAdmin;
    return canView(item.permKey);
  });

  // ─── Dynamic role display ─────────────────────────────────
  const getDisplayRole = () => {
    if (isSuperAdmin) return adminData.role || 'Super Admin';
    if (adminData.role) return adminData.role;
    return roleName || storedUserRole || '';
  };

  // ─── Dynamic badge color — works for ALL roles ────────────
  const getRoleBadgeStyle = () => {
    const r = (getDisplayRole() || '').toLowerCase().replace(/\s+/g, '_');

    if (r.includes('super'))                    return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
    if (r === 'admin')                          return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
    if (r.includes('dev'))                      return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
    if (r === 'it' || r === 'tech' || r.includes('technical')) 
                                                return 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30';
    if (r.includes('market'))                   return 'bg-pink-500/20 text-pink-300 border border-pink-500/30';
    if (r.includes('staff'))                    return 'bg-green-500/20 text-green-300 border border-green-500/30';
    if (r.includes('support'))                  return 'bg-teal-500/20 text-teal-300 border border-teal-500/30';
    if (r.includes('finance'))                  return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
    if (r.includes('hr'))                       return 'bg-rose-500/20 text-rose-300 border border-rose-500/30';
    if (r.includes('manager') || r.includes('supervisor'))
                                                return 'bg-orange-500/20 text-orange-300 border border-orange-500/30';
    if (r.includes('analyst'))                  return 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30';
    if (r.includes('qa'))                       return 'bg-lime-500/20 text-lime-300 border border-lime-500/30';
    if (r.includes('sales'))                    return 'bg-amber-500/20 text-amber-300 border border-amber-500/30';
    if (r.includes('design'))                   return 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30';
    if (r.includes('security'))                 return 'bg-red-500/20 text-red-300 border border-red-500/30';
    if (r.includes('content'))                  return 'bg-sky-500/20 text-sky-300 border border-sky-500/30';
    if (r.includes('operat'))                   return 'bg-violet-500/20 text-violet-300 border border-violet-500/30';
    if (r.includes('team'))                     return 'bg-green-500/20 text-green-300 border border-green-500/30';

    // fallback — any other role gets a neutral badge
    return 'bg-slate-600/40 text-slate-300 border border-slate-500/30';
  };

  return (
    <>
      {isMobile && isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm transition-opacity"
        />
      )}

      <div className={`
        fixed top-0 left-0 h-full bg-slate-900 text-white shadow-2xl z-50
        transition-all duration-300 ease-in-out flex flex-col border-r border-slate-800
        ${isSidebarOpen ? 'w-64' : 'w-20'}
        ${isMobile && !isSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
      `}>

        {/* HEADER */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          {isSidebarOpen && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                <img src={logo} alt="ORCA" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <span className="font-bold text-base text-gray-100 block">Helpdesk</span>
                {!loading && getDisplayRole() && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeStyle()}`}>
                    {getDisplayRole()}
                  </span>
                )}
                {loading && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 size={10} className="animate-spin" />
                    Loading...
                  </span>
                )}
              </div>
            </div>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 hover:bg-slate-800 rounded-lg transition"
          >
            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* NAV */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-2">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-10 bg-slate-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            visibleMenus.map((item, index) => {
              const Icon = item.icon;
              const isExpanded = expandedMenus[item.id];

              const visibleSubmenu = item.submenu?.filter(sub =>
                isSuperAdmin || canView(sub.permKey)
              ) || [];

              const isActive =
                currentPage === item.id ||
                visibleSubmenu.some(sub => sub.id === currentPage);

              if (item.hasSubmenu && visibleSubmenu.length === 0) return null;

              const prevItem = visibleMenus[index - 1];
              const showDivider = item.superAdminOnly && prevItem && !prevItem.superAdminOnly;

              return (
                <div key={item.id}>
                  {showDivider && isSidebarOpen && (
                    <div className="px-2 pt-3 pb-1">
                      <div className="border-t border-slate-700/80 pt-2 flex items-center gap-2">
                        <Shield size={11} className="text-yellow-500/60" />
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                          System
                        </span>
                      </div>
                    </div>
                  )}
                  {showDivider && !isSidebarOpen && (
                    <div className="px-2 pt-2 pb-1">
                      <div className="border-t border-slate-700/80" />
                    </div>
                  )}

                  <button
                    onClick={() => {
                      if (item.hasSubmenu && isSidebarOpen && visibleSubmenu.length > 0) {
                        toggleSubmenu(item.id);
                      } else {
                        handlePageChange(item.id);
                        if (isMobile) setIsSidebarOpen(false);
                      }
                    }}
                    title={!isSidebarOpen ? item.label : undefined}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                      transition-all duration-200
                      ${isActive
                        ? item.superAdminOnly
                          ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                          : 'bg-blue-600 text-white shadow-md shadow-blue-900/40'
                        : item.superAdminOnly
                          ? 'text-yellow-500/60 hover:bg-yellow-500/10 hover:text-yellow-400'
                          : 'text-gray-400 hover:bg-slate-800 hover:text-gray-200'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon size={20} className="flex-shrink-0" />
                      {isSidebarOpen && (
                        <span className="text-sm font-medium truncate">{item.label}</span>
                      )}
                    </div>
                    {item.hasSubmenu && isSidebarOpen && visibleSubmenu.length > 0 && (
                      <ChevronDown
                        size={16}
                        className={`flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    )}
                  </button>

                  {item.hasSubmenu && isExpanded && isSidebarOpen && (
                    <div className="mt-1 ml-8 space-y-1">
                      {visibleSubmenu.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => {
                            handlePageChange(sub.id);
                            if (isMobile) setIsSidebarOpen(false);
                          }}
                          className={`
                            w-full text-left px-3 py-2 rounded-lg text-sm transition-all
                            ${currentPage === sub.id
                              ? 'bg-slate-800 text-blue-400 font-medium'
                              : 'text-gray-500 hover:bg-slate-800 hover:text-gray-300'
                            }
                          `}
                        >
                          <div className="flex items-center gap-2">
                            <ChevronRight size={14} />
                            {sub.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        {/* LOGOUT */}
        {!isMobile && (
          <div className="p-3 border-t border-slate-800">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                bg-slate-800 hover:bg-red-600/80 text-gray-400 hover:text-white transition-all"
              title={!isSidebarOpen ? 'Logout' : undefined}
            >
              <LogOut size={20} className="flex-shrink-0" />
              {isSidebarOpen && <span className="text-sm font-medium">Logout</span>}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar; 